import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, ChevronRight, ChevronLeft, Loader2, CheckCircle, AlertTriangle, FileText } from 'lucide-react';
import AGBPage from '../AGBPage';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const CUSTOMER_TOKEN_KEY = 'enbet_customer_token';
const portalHeaders = () => {
  const t = localStorage.getItem(CUSTOMER_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * 3-Schritte-Wizard für Rechnungs-Stammdaten + AGB-Zustimmung.
 * Nicht-blockierend: Kunde kann das Modal jederzeit schließen, Banner bleibt aber sichtbar
 * bis alles vollständig ist.
 */
export default function BillingOnboardingModal({ onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showAgbReader, setShowAgbReader] = useState(false);
  const [termsVersion, setTermsVersion] = useState('');
  const [agbCheck, setAgbCheck] = useState(false);

  const [form, setForm] = useState({
    billing_company: '',
    billing_legal_form: '',
    billing_street: '',
    billing_postal_code: '',
    billing_city: '',
    billing_country: 'DE',
    contact_name: '',
    contact_position: '',
    billing_email: '',
    phone: '',
    vat_id: '',
    tax_number: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/portal/me/billing-profile`, { headers: portalHeaders() });
      setForm((f) => ({
        ...f,
        billing_company: data.billing_company || '',
        billing_legal_form: data.billing_legal_form || '',
        billing_street: data.billing_street || '',
        billing_postal_code: data.billing_postal_code || '',
        billing_city: data.billing_city || '',
        billing_country: data.billing_country || 'DE',
        contact_name: data.contact_name || '',
        contact_position: data.contact_position || '',
        billing_email: data.billing_email || '',
        phone: data.phone || '',
        vat_id: data.vat_id || '',
        tax_number: data.tax_number || '',
      }));
      setTermsVersion(data.current_terms_version || '');
      // Wenn Terms aktueller Version schon akzeptiert: agbCheck vorbelegen
      if (data.terms_version === data.current_terms_version && data.terms_accepted_at) {
        setAgbCheck(true);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveStep = async (advance) => {
    setSaving(true); setError('');
    try {
      // Bei Schritt 1+2 nur PATCH, bei Schritt 3 accept-terms
      if (step === 1 || step === 2) {
        await axios.patch(`${API}/portal/me/billing-profile`, form, { headers: portalHeaders() });
        if (advance) setStep(step + 1);
      } else if (step === 3) {
        if (!agbCheck) { setError('Bitte AGB ausdrücklich zustimmen.'); setSaving(false); return; }
        await axios.post(`${API}/portal/me/accept-terms`, { terms_version: termsVersion }, { headers: portalHeaders() });
        if (onComplete) onComplete();
        onClose();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  const upd = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Schritt 1: Firmendaten
  const stepFirm = (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label>Firmenname *</Label>
          <Input value={form.billing_company} onChange={upd('billing_company')} placeholder="z.B. Mustermann GmbH" />
        </div>
        <div>
          <Label>Rechtsform *</Label>
          <select value={form.billing_legal_form} onChange={upd('billing_legal_form')}
            className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
            <option value="">— wählen —</option>
            <option value="GmbH">GmbH</option>
            <option value="UG (haftungsbeschränkt)">UG (haftungsbeschränkt)</option>
            <option value="GbR">GbR</option>
            <option value="OHG">OHG</option>
            <option value="KG">KG</option>
            <option value="GmbH & Co. KG">GmbH & Co. KG</option>
            <option value="e.K.">e.K.</option>
            <option value="Einzelunternehmer">Einzelunternehmer</option>
            <option value="AG">AG</option>
            <option value="Sonstige">Sonstige</option>
          </select>
        </div>
      </div>
      <div>
        <Label>Straße + Hausnr. *</Label>
        <Input value={form.billing_street} onChange={upd('billing_street')} placeholder="z.B. Hauptstraße 12" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>PLZ *</Label>
          <Input value={form.billing_postal_code} onChange={upd('billing_postal_code')} placeholder="12345" />
        </div>
        <div className="md:col-span-2">
          <Label>Ort *</Label>
          <Input value={form.billing_city} onChange={upd('billing_city')} placeholder="Musterstadt" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Land</Label>
          <select value={form.billing_country} onChange={upd('billing_country')}
            className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]">
            <option value="DE">Deutschland</option>
            <option value="AT">Österreich</option>
            <option value="CH">Schweiz</option>
            <option value="EU">EU (andere)</option>
            <option value="Sonstige">Sonstige</option>
          </select>
        </div>
        <div>
          <Label>Telefon</Label>
          <Input value={form.phone} onChange={upd('phone')} placeholder="+49 ..." />
        </div>
      </div>
    </div>
  );

  // Schritt 2: Rechnungs-Daten + Ansprechpartner
  const stepBill = (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Ansprechpartner *</Label>
          <Input value={form.contact_name} onChange={upd('contact_name')} placeholder="Vor- und Nachname" />
        </div>
        <div>
          <Label>Position / Funktion</Label>
          <Input value={form.contact_position} onChange={upd('contact_position')} placeholder="z.B. Geschäftsführung" />
        </div>
      </div>
      <div>
        <Label>Rechnungs-E-Mail *</Label>
        <Input type="email" value={form.billing_email} onChange={upd('billing_email')} placeholder="rechnung@firma.de" />
        <p className="text-[10px] text-gray-500 mt-1">An diese Adresse gehen Rechnungen. Kann von Ihrer Portal-Login-E-Mail abweichen.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>USt-IdNr.</Label>
          <Input value={form.vat_id} onChange={upd('vat_id')} placeholder="DE123456789" />
        </div>
        <div>
          <Label>Steuernummer</Label>
          <Input value={form.tax_number} onChange={upd('tax_number')} placeholder="123/456/78901" />
        </div>
      </div>
      <p className="text-[11px] text-gray-500 italic">
        Bitte mindestens USt-IdNr. oder Steuernummer angeben. Pflicht nach § 14 UStG für B2B-Rechnungen.
      </p>
    </div>
  );

  // Schritt 3: AGB-Zustimmung
  const stepAgb = (
    <div className="space-y-4">
      <div className="bg-gray-50 border border-gray-200 p-4 text-sm">
        <p className="font-bold text-[#1f1f1f] mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
          <FileText size={14} className="inline mr-1" /> Allgemeine Geschäftsbedingungen, Version {termsVersion}
        </p>
        <p className="text-gray-600 text-xs mb-3">
          Bitte lesen Sie unsere AGB und bestätigen Sie diese ausdrücklich. Sie können die AGB jederzeit unter
          /agb auf unserer Website nochmal einsehen.
        </p>
        <button type="button" onClick={() => setShowAgbReader(true)}
          className="text-xs uppercase tracking-wider border border-gray-400 text-gray-700 px-3 py-1.5 hover:bg-gray-100"
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          AGB jetzt lesen
        </button>
      </div>

      <label className="flex items-start gap-3 cursor-pointer p-3 bg-white border border-gray-300 hover:border-[#658a07]">
        <input type="checkbox" checked={agbCheck} onChange={(e) => setAgbCheck(e.target.checked)}
          className="mt-0.5 w-4 h-4 flex-shrink-0" />
        <span className="text-sm text-[#1f1f1f]">
          Ich habe die AGB (Version {termsVersion}) <strong>gelesen</strong> und stimme der Verarbeitung
          meiner Daten gemäß diesen Bedingungen <strong>ausdrücklich zu</strong>.
        </span>
      </label>

      <p className="text-[11px] text-gray-500">
        Hinweis: Ihre Zustimmung wird mit Zeitstempel und IP-Adresse erfasst (Beweispflicht nach DSGVO/BGB).
        Bei späteren AGB-Änderungen werden Sie hier erneut um Zustimmung gebeten.
      </p>
    </div>
  );

  const currentStep = step === 1 ? stepFirm : step === 2 ? stepBill : stepAgb;
  const stepTitle = step === 1 ? 'Firmendaten' : step === 2 ? 'Rechnungs-Daten' : 'AGB-Zustimmung';
  const stepDesc = step === 1
    ? 'Damit wir saubere Rechnungen und Auftragsbestätigungen für Sie erstellen können.'
    : step === 2
    ? 'Wer ist Ihr Ansprechpartner und wo geht die Rechnung hin?'
    : 'Letzter Schritt: ausdrückliche Zustimmung zu unseren Bedingungen.';

  return (
    <>
      <div className="fixed inset-0 z-[150] bg-black/70 flex items-center justify-center p-4 overflow-y-auto"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="bg-white w-full max-w-[680px] max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-[#0a0a0a] text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
            <div>
              <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Schritt {step} / 3 · {stepTitle}
              </p>
              <h3 className="text-lg font-bold uppercase mt-0.5" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Ihre Daten vervollständigen
              </h3>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white" title="Später vervollständigen"><X size={20} /></button>
          </div>

          {/* Progress */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center gap-2">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`flex-1 h-1 ${n <= step ? 'bg-[#658a07]' : 'bg-gray-200'}`} />
            ))}
          </div>

          {/* Body */}
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">{stepDesc}</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 text-sm mb-4">
                <AlertTriangle size={14} className="inline mr-2" /> {error}
              </div>
            )}

            {loading ? (
              <div className="py-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div>
            ) : currentStep}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between sticky bottom-0">
            <button onClick={step > 1 ? () => setStep(step - 1) : onClose}
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f] px-3 py-2 disabled:opacity-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {step > 1 ? <><ChevronLeft size={14} /> Zurück</> : 'Später'}
            </button>
            <button onClick={() => saveStep(step < 3)} disabled={saving || (step === 3 && !agbCheck)}
              className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-5 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {step < 3 ? <>Speichern + weiter <ChevronRight size={14} /></> : <><CheckCircle size={14} /> Bestätigen + abschließen</>}
            </button>
          </div>
        </div>
      </div>

      {showAgbReader && (
        <div className="fixed inset-0 z-[200] bg-white overflow-y-auto">
          <AGBPage onClose={() => setShowAgbReader(false)} />
        </div>
      )}
    </>
  );
}

function Label({ children }) {
  return <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">{children}</label>;
}
function Input(props) {
  return <input {...props} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />;
}
